import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { propertyData, galleryMedia } from '../mockData';
import { galleryCategories } from '../mockData_extended';
import { X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { trackGalleryView } from '../utils/analytics';

// Helper to get translated alt text
const getAltText = (media, language) => {
  if (typeof media.alt === 'string') {
    return media.alt;
  }
  return media.alt[language] || media.alt.en;
};

const Gallery = () => {
  const { language } = useLanguage();
  const content = (propertyData[language] || propertyData.en).gallery;
  const categories = galleryCategories[language] || galleryCategories.en;
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState(0);

  // Get media items for current category
  const getCategoryMedia = (categoryIndex) => {
    return galleryMedia.filter(media => media.category === categoryIndex);
  };

  const displayedMedia = getCategoryMedia(activeCategory);

  // Get all media for the current category for lightbox navigation
  const currentCategoryMedia = getCategoryMedia(activeCategory);

  const openLightbox = (localIndex) => {
    // Track gallery image view
    const categoryName = categories[activeCategory] || 'unknown';
    trackGalleryView(categoryName, localIndex);
    setSelectedMediaIndex(localIndex);
  };

  const closeLightbox = () => {
    setSelectedMediaIndex(null);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    setSelectedMediaIndex((prev) => 
      prev === 0 ? currentCategoryMedia.length - 1 : prev - 1
    );
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setSelectedMediaIndex((prev) => 
      prev === currentCategoryMedia.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section id="gallery" className="py-16 sm:py-24 bg-white" aria-labelledby="gallery-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-800 mb-6 sm:mb-8 text-center" id="gallery-heading">
          {content.title}
        </h2>

        {/* Category Tabs - Scrollable on mobile */}
        <div className="relative mb-8 sm:mb-12">
          <div className="flex overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap sm:justify-center gap-2 sm:gap-3 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setActiveCategory(index)}
                className={`flex-shrink-0 px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeCategory === index
                    ? 'bg-gradient-to-r from-[#d4845f] to-[#c17552] text-white shadow-lg scale-105'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {displayedMedia.map((media, index) => (
            <GalleryItem3D
              key={index}
              media={media}
              index={index}
              language={language}
              onClick={() => openLightbox(index)}
            />
          ))}
        </div>
      </div>

      {/* Lightbox with 3D effect and navigation */}
      {selectedMediaIndex !== null && currentCategoryMedia[selectedMediaIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 backdrop-blur-xl animate-fadeIn"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-[#d4845f] transition-all duration-300 hover:scale-125 hover:rotate-90 z-50"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous Button */}
          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white hover:text-[#d4845f] transition-all duration-300 hover:scale-125 z-50 bg-black/30 hover:bg-black/50 rounded-full p-3"
            onClick={goToPrevious}
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          {/* Next Button */}
          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white hover:text-[#d4845f] transition-all duration-300 hover:scale-125 z-50 bg-black/30 hover:bg-black/50 rounded-full p-3"
            onClick={goToNext}
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          {/* Media Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-lg z-50">
            {selectedMediaIndex + 1} / {currentCategoryMedia.length}
          </div>

          {/* Display Image or Video */}
          {currentCategoryMedia[selectedMediaIndex].type === 'image' ? (
            <img
              src={currentCategoryMedia[selectedMediaIndex].url}
              alt={getAltText(currentCategoryMedia[selectedMediaIndex], language)}
              className="max-w-full max-h-full object-contain animate-scaleIn shadow-2xl"
              style={{
                transform: 'perspective(1000px) translateZ(50px)',
                boxShadow: '0 40px 100px rgba(0,0,0,0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <video
              src={currentCategoryMedia[selectedMediaIndex].url}
              controls
              autoPlay
              className="max-w-full max-h-full object-contain animate-scaleIn shadow-2xl"
              style={{
                transform: 'perspective(1000px) translateZ(50px)',
                boxShadow: '0 40px 100px rgba(0,0,0,0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      )}
    </section>
  );
};

const GalleryItem3D = ({ media, index, language, onClick }) => {
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const altText = getAltText(media, language);

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;

    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotation({ x: 0, y: 0 });
    setIsHovered(false);
  };

  return (
    <div
      className="relative aspect-[4/3] overflow-hidden rounded-lg cursor-pointer perspective-1000 group"
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{
        animationDelay: `${index * 0.05}s`
      }}
    >
      {/* Loading placeholder */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-slate-200 animate-pulse flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-[#d4845f] rounded-full animate-spin"></div>
        </div>
      )}
      
      {media.type === 'image' ? (
        <img
          src={media.thumbnail}
          alt={altText}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          className={`w-full h-full object-cover transition-all duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            transform: `
              perspective(1000px) 
              rotateX(${rotation.x}deg) 
              rotateY(${rotation.y}deg) 
              scale(${isHovered ? 1.15 : 1})
              translateZ(${isHovered ? '30px' : '0px'})
            `,
            filter: isHovered ? 'brightness(1.1)' : 'brightness(1)'
          }}
        />
      ) : (
        <>
          <img
            src={media.thumbnail}
            alt={altText}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            className={`w-full h-full object-cover transition-all duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{
              transform: `
                perspective(1000px) 
                rotateX(${rotation.x}deg) 
                rotateY(${rotation.y}deg) 
                scale(${isHovered ? 1.15 : 1})
                translateZ(${isHovered ? '30px' : '0px'})
              `,
              filter: isHovered ? 'brightness(1.1)' : 'brightness(1)'
            }}
          />
          {/* Video Play Icon Overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white/90 rounded-full p-3 sm:p-4 transform transition-all duration-300 group-hover:scale-110">
              <Play className="w-6 h-6 sm:w-8 sm:h-8 text-[#d4845f]" fill="currentColor" />
            </div>
          </div>
        </>
      )}
      
      {/* Gradient overlay - always visible for caption readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
      
      {/* Caption at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3">
        <p className="text-white text-xs sm:text-sm font-medium drop-shadow-lg line-clamp-2">
          {altText}
        </p>
      </div>
    </div>
  );
};

export default Gallery;